import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Bell, BookOpen, CheckCircle2, ChevronRight,
  Coins, FileText, Film, Lightbulb, MessageCircle, PenLine, RefreshCw,
  Save, Send, Sparkles, Upload, Zap,
} from 'lucide-react';
import { useTeamBrand } from '../vnext/branding/TeamBrandContext';
import XiaoyingMark from '../vnext/components/XiaoyingMark';
import '../vnext/components/XiaoyingMark.css';

const PRIMARY = 'bg-[#8c7db0] hover:bg-[#7d6ca3] dark:bg-[#8f7fd0] dark:hover:bg-[#9b8bd8]';
const CARD = 'bg-white/88 dark:bg-[#14141a]/92 border border-black/[0.06] dark:border-white/[0.08] shadow-sm shadow-stone-200/70 dark:shadow-none';
const SOFT = 'bg-white/62 dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08]';

const GENRE_GROUPS = [
  {
    title: '题材',
    items: ['仙侠逆袭', '现代甜宠', '悬疑反转', '都市异能', '职场爽剧', '复仇成长'],
  },
  {
    title: '主角处境',
    items: ['被背叛', '身份隐藏', '穿越重生', '被退婚', '替嫁', '被误解'],
  },
  {
    title: '核心关系',
    items: ['师徒', '死对头', '契约夫妻', '前任', '真假千金', '主仆反转'],
  },
  {
    title: '爽点',
    items: ['打脸', '复仇', '身份揭露', '追妻火葬场', '逆袭封神', '连续反转'],
  },
];

const QUICK_STARTS = [
  '我没有明确想法，请先给我三个短剧方向',
  '我想写一个复仇爽剧，但不知道主角是谁',
  '我有一句话设定，帮我变成能写的项目',
  '我想找一个适合 9:16 视频化的故事',
];

const DIRECTION_SETS = [
  {
    title: '仙门弃徒复仇',
    type: '强爽感 · 仙侠逆袭',
    hook: '少年被宗门当成废棋逐出，却发现自己才是被封印的旧日剑主。',
    why: '第一集就能给出背叛、落差和身份悬念，适合快速抓住用户。',
  },
  {
    title: '替嫁女主反控全局',
    type: '甜宠反转 · 关系拉扯',
    hook: '替嫁新娘本想逃婚，却发现病弱丈夫一直在暗中替她清算仇人。',
    why: '兼顾情绪关系和爽点反转，容易形成连续追看动机。',
  },
  {
    title: '被裁员工证据反杀',
    type: '职场爽剧 · 现实压迫',
    hook: '新人被高管栽赃背锅，意外拿到公司旧案证据链，开始逐层反杀。',
    why: '现实代入强，冲突直接，适合做 30 集短爽结构。',
  },
];

const STEPS = [
  { title: '找方向', desc: '题材、处境、关系' },
  { title: '定冲突', desc: '敌人、代价、目标' },
  { title: '做方案', desc: '卖点、集数、首集钩子' },
  { title: '进写作', desc: '创建项目并写剧本' },
];

function TopBar() {
  const navigate = useNavigate();
  const brand = useTeamBrand();

  return (
    <header className="h-16 shrink-0 bg-white/86 dark:bg-[#0c0c10]/90 backdrop-blur-xl border-b border-black/[0.06] dark:border-white/[0.07]">
      <div className="max-w-[1500px] mx-auto px-6 h-full flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/studio')}
            className="w-9 h-9 rounded-full border border-black/10 dark:border-white/10 flex items-center justify-center hover:bg-stone-50 dark:hover:bg-white/[0.05]"
          >
            <ArrowLeft size={17} />
          </button>
          <div className="flex items-center gap-2.5">
            {brand.active ? <XiaoyingMark size={32} /> : <img src="/xiaoying-mark.svg" alt={brand.name} className="w-8 h-8 rounded-xl object-contain" />}
            <span className="font-bold text-[17px]">{brand.name}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-[#eee9de] dark:bg-white/[0.07] text-stone-500">创意 Agent</span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 text-xs text-stone-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08]">
            <Save size={13} /> 自动保存中
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Coins size={13} /> 736 点
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button className="hidden sm:inline-flex px-4 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-semibold hover:bg-stone-50 dark:hover:bg-white/[0.05]">
            我的创作
          </button>
          <button className="w-9 h-9 rounded-full border border-black/10 dark:border-white/10 flex items-center justify-center">
            <Bell size={16} />
          </button>
          <button className="w-9 h-9 rounded-full bg-stone-950 text-white dark:bg-white dark:text-stone-950 text-sm font-semibold">
            A
          </button>
        </div>
      </div>
    </header>
  );
}

function StepRail() {
  return (
    <div className={`${CARD} rounded-3xl p-4`}>
      <div className="grid md:grid-cols-4 gap-3">
        {STEPS.map((step, index) => (
          <div key={step.title} className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${
              index === 0 ? 'bg-[#8c7db0] text-white' : 'bg-[#eee9de] dark:bg-white/[0.07] text-stone-500'
            }`}>
              {index + 1}
            </div>
            <div>
              <p className="text-sm font-bold">{step.title}</p>
              <p className="text-xs text-stone-500 dark:text-gray-400">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InspirationPanel({ selected, toggle, applyQuickStart }) {
  const brand = useTeamBrand();
  return (
    <aside className="lg:sticky lg:top-20 self-start space-y-4">
      <div className={`${CARD} rounded-3xl p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb size={17} className="text-[#8c7db0]" />
          <h2 className="font-black">灵感启动</h2>
        </div>
        <p className="text-xs text-stone-500 dark:text-gray-400 leading-relaxed mb-4">
          {brand.text('没思路时不要盯着空白输入框。先点几个标签，小影会帮你收敛成能写的短剧方向。')}
        </p>

        <div className="space-y-5">
          {GENRE_GROUPS.map(group => (
            <section key={group.title}>
              <h3 className="text-xs font-bold text-stone-400 mb-2">{group.title}</h3>
              <div className="flex flex-wrap gap-2">
                {group.items.map(item => {
                  const active = selected.includes(item);
                  return (
                    <button
                      key={item}
                      onClick={() => toggle(item)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? 'bg-[#8c7db0] text-white border-[#8c7db0]'
                          : 'bg-white/70 dark:bg-white/[0.04] border-black/[0.07] dark:border-white/[0.08] text-stone-600 dark:text-gray-300 hover:border-[#8c7db0]/60'
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className={`${CARD} rounded-3xl p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <Zap size={17} className="text-amber-600" />
          <h2 className="font-black">常用开局</h2>
        </div>
        <div className="space-y-2">
          {QUICK_STARTS.map(item => (
            <button
              key={item}
              onClick={() => applyQuickStart(item)}
              className="w-full text-left p-3 rounded-2xl bg-[#f7f4ee] dark:bg-white/[0.04] hover:bg-[#eee9de] dark:hover:bg-white/[0.07] text-xs leading-relaxed text-stone-600 dark:text-gray-300"
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className={`${SOFT} rounded-3xl p-5`}>
        <button className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-stone-300 dark:border-white/15 text-sm font-semibold text-stone-600 dark:text-gray-300">
          <Upload size={16} /> 导入参考剧本 / 资料
        </button>
      </div>
    </aside>
  );
}

function DirectionCard({ item, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-3xl p-4 border transition ${
        active
          ? 'bg-[#f1edf8] dark:bg-[#8c7db0]/15 border-[#8c7db0]'
          : 'bg-white/76 dark:bg-white/[0.04] border-black/[0.06] dark:border-white/[0.08] hover:border-[#8c7db0]/60'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-black">{item.title}</p>
          <p className="text-xs text-[#8c7db0] dark:text-[#b9afe7] mt-1">{item.type}</p>
        </div>
        {active ? <CheckCircle2 size={18} className="text-[#8c7db0]" /> : <ChevronRight size={18} className="text-stone-300" />}
      </div>
      <p className="text-sm text-stone-700 dark:text-gray-300 leading-relaxed mb-2">{item.hook}</p>
      <p className="text-xs text-stone-500 dark:text-gray-400 leading-relaxed">{item.why}</p>
    </button>
  );
}

function AgentWorkspace({
  idea,
  setIdea,
  selected,
  activeDirection,
  setActiveDirection,
  messages,
  submitIdea,
}) {
  const brand = useTeamBrand();
  const selectedText = selected.length ? selected.join('、') : '未选择标签';

  return (
    <main className="space-y-4">
      <div className={`${CARD} rounded-3xl overflow-hidden`}>
        <div className="px-5 py-4 border-b border-black/[0.06] dark:border-white/[0.07] flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black">你想写一个什么故事？</h1>
            <p className="text-xs text-stone-500 dark:text-gray-400 mt-1">可以只说一句话，也可以先点左侧灵感标签。</p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-stone-500">
            <MessageCircle size={14} /> 当前草稿：新创意
          </div>
        </div>

        <div className="p-5">
          <textarea
            value={idea}
            onChange={e => setIdea(e.target.value)}
            rows={4}
            placeholder="比如：一个被全宗门背叛的少年，重生后靠隐藏身份复仇……"
            className="w-full resize-none outline-none bg-[#f8f5ef] dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08] rounded-3xl px-5 py-4 text-sm leading-relaxed placeholder:text-stone-400"
          />

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
              <span className="px-3 py-1.5 rounded-full bg-[#eee9de] dark:bg-white/[0.06]">已选：{selectedText}</span>
              <span className="px-3 py-1.5 rounded-full bg-[#eee9de] dark:bg-white/[0.06]">9:16 短剧</span>
              <span className="px-3 py-1.5 rounded-full bg-[#eee9de] dark:bg-white/[0.06]">默认 30 集</span>
            </div>
            <button
              onClick={submitIdea}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-2xl ${PRIMARY} text-white text-sm font-bold shadow-lg shadow-violet-400/20`}
            >
              帮我找方向 <Sparkles size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className={`${CARD} rounded-3xl p-5`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-black">{brand.text('小影给你的 3 个方向')}</h2>
            <p className="text-xs text-stone-500 dark:text-gray-400 mt-1">先选方向，不急着写正文。</p>
          </div>
          <button className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-stone-900 dark:hover:text-white">
            <RefreshCw size={14} /> 换一批
          </button>
        </div>

        <div className="space-y-3">
          {DIRECTION_SETS.map(item => (
            <DirectionCard
              key={item.title}
              item={item}
              active={activeDirection.title === item.title}
              onClick={() => setActiveDirection(item)}
            />
          ))}
        </div>
      </div>

      <div className={`${CARD} rounded-3xl p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-[#8c7db0]" />
          <h2 className="font-black">Agent 对话</h2>
        </div>

        <div className="space-y-3 mb-4">
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[84%] rounded-3xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-[#8c7db0] text-white rounded-br-md'
                  : 'bg-[#f7f4ee] dark:bg-white/[0.05] text-stone-700 dark:text-gray-300 rounded-bl-md'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            value={idea}
            onChange={e => setIdea(e.target.value)}
            placeholder={brand.text('继续补充想法，或让小影继续追问...')}
            className="flex-1 input text-sm"
          />
          <button onClick={submitIdea} className={`w-11 h-11 rounded-2xl ${PRIMARY} text-white flex items-center justify-center`}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </main>
  );
}

function SchemePanel({ selected, activeDirection, idea }) {
  const navigate = useNavigate();

  const brief = useMemo(() => {
    return [
      `项目名：${activeDirection.title}`,
      `一句话故事：${activeDirection.hook}`,
      `题材方向：${activeDirection.type}`,
      `用户补充：${idea || '暂无'}`,
      `已选标签：${selected.length ? selected.join('、') : '暂无'}`,
      '建议下一步：进入写剧本，先生成故事圣经和 1-3 集分集大纲。',
    ].join('\n');
  }, [activeDirection, idea, selected]);

  const confirm = () => {
    const params = new URLSearchParams({
      idea: activeDirection.hook,
      brief,
    });
    navigate(`/writing?${params.toString()}`);
  };

  return (
    <aside className="lg:sticky lg:top-20 self-start space-y-4">
      <div className={`${CARD} rounded-3xl overflow-hidden`}>
        <div className="h-40 bg-[radial-gradient(circle_at_68%_34%,rgba(255,214,156,0.55),transparent_28%),linear-gradient(135deg,#17130f_0%,#7c4f38_48%,#1b1b22_100%)] relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
          <div className="absolute left-5 bottom-5 text-white">
            <p className="text-xs text-white/70 mb-1">当前方案</p>
            <h2 className="text-2xl font-black">{activeDirection.title}</h2>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <section>
            <h3 className="text-xs font-bold text-stone-400 mb-2">一句话故事</h3>
            <p className="text-sm leading-relaxed text-stone-800 dark:text-gray-200">{activeDirection.hook}</p>
          </section>

          <section>
            <h3 className="text-xs font-bold text-stone-400 mb-2">短剧卖点</h3>
            <div className="grid grid-cols-2 gap-2">
              {['强冲突开局', '身份悬念', '连续打脸', '首集钩子'].map(item => (
                <div key={item} className="px-3 py-2 rounded-2xl bg-[#f7f4ee] dark:bg-white/[0.04] text-xs font-semibold text-stone-600 dark:text-gray-300">
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-xs font-bold text-stone-400 mb-2">方案完整度</h3>
            <div className="space-y-2">
              {[
                ['题材方向', true],
                ['主角处境', true],
                ['核心冲突', true],
                ['第一集钩子', false],
              ].map(([label, done]) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-stone-600 dark:text-gray-300">{label}</span>
                  {done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <span className="text-xs text-amber-600">待追问</span>}
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-2 gap-2">
            <button className="py-3 rounded-2xl border border-black/[0.08] dark:border-white/[0.1] text-sm font-semibold hover:bg-stone-50 dark:hover:bg-white/[0.06]">
              继续打磨
            </button>
            <button className="py-3 rounded-2xl border border-black/[0.08] dark:border-white/[0.1] text-sm font-semibold hover:bg-stone-50 dark:hover:bg-white/[0.06]">
              换方向
            </button>
          </div>

          <button
            onClick={confirm}
            className={`w-full py-3.5 rounded-2xl ${PRIMARY} text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg shadow-violet-400/20`}
          >
            确认方案，进入写剧本 <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <div className={`${SOFT} rounded-3xl p-5`}>
        <h3 className="font-black mb-3">接下来会生成</h3>
        <div className="space-y-2 text-sm text-stone-600 dark:text-gray-300">
          <p className="flex items-center gap-2"><BookOpen size={15} /> 故事圣经</p>
          <p className="flex items-center gap-2"><FileText size={15} /> 分集大纲</p>
          <p className="flex items-center gap-2"><PenLine size={15} /> 第一集正文</p>
          <p className="flex items-center gap-2"><Film size={15} /> 后续可进画布做视频</p>
        </div>
      </div>
    </aside>
  );
}

export default function CreativeAgentPage() {
  const [searchParams] = useSearchParams();
  const initialIdea = searchParams.get('idea') || '';
  const [idea, setIdea] = useState(initialIdea);
  const [selected, setSelected] = useState(['仙侠逆袭', '被背叛', '身份隐藏']);
  const [activeDirection, setActiveDirection] = useState(DIRECTION_SETS[0]);
  const [messages, setMessages] = useState([
    {
      role: 'ai',
      text: '你不用先想清楚完整故事。先告诉我一个感觉、一种题材，或者点左侧标签。我会给你几个能继续写下去的方向。',
    },
  ]);

  const toggle = item => {
    setSelected(prev => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);
  };

  const applyQuickStart = text => {
    setIdea(text);
    setMessages(prev => [...prev, { role: 'user', text }, {
      role: 'ai',
      text: '可以。我先按“短剧容易开局、冲突清楚、第一集有钩子”的标准给你 3 个方向。你选一个后，我再追问主角、敌人和第一集结尾。',
    }]);
  };

  const submitIdea = () => {
    const text = idea.trim() || '我没有明确想法，请先帮我找方向。';
    setMessages(prev => [...prev, { role: 'user', text }, {
      role: 'ai',
      text: `我会按这些条件收敛：${selected.length ? selected.join('、') : '短剧强冲突'}。目前最适合先选一个方向，再补“主角最惨处境”和“第一集结尾钩子”。`,
    }]);
  };

  return (
    <div className="min-h-screen bg-[#f7f4ee] dark:bg-[#0e0e10] text-stone-950 dark:text-gray-100">
      <TopBar />
      <div className="max-w-[1500px] mx-auto px-6 py-6 space-y-5">
        <StepRail />
        <div className="grid lg:grid-cols-[310px_minmax(0,1fr)_360px] gap-5 items-start">
          <InspirationPanel selected={selected} toggle={toggle} applyQuickStart={applyQuickStart} />
          <AgentWorkspace
            idea={idea}
            setIdea={setIdea}
            selected={selected}
            activeDirection={activeDirection}
            setActiveDirection={setActiveDirection}
            messages={messages}
            submitIdea={submitIdea}
          />
          <SchemePanel selected={selected} activeDirection={activeDirection} idea={idea} />
        </div>
      </div>
    </div>
  );
}
